import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, inr } from "@/lib/api";
import { Badge2 } from "@/components/common";

function DiscountCard({ value, onSaved }) {
  const [d, setD] = useState(value);
  useEffect(() => { setD(value); }, [value]);
  const save = async () => {
    try { await api.put("/admin/agent-discount", { enabled: d.enabled, percent: Number(d.percent) }); toast.success("Agent discount saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="lx-card space-y-3 max-w-xl" data-testid="admin-agent-discount-card">
      <div className="font-semibold">Marketing Agent discount</div>
      <p className="text-xs text-slate-500">Applied automatically, before tax, to plans, AI credit packs, DIY website payments, shoot advances and LaunchX360 campaign convenience fees for companies managed by an active agent. Never applied to Meta ad spend, taxes or recurring third-party charges. Coupons stack with it only when the coupon's "Combine with agent discount" switch is on; otherwise the bigger saving wins.</p>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm"><Switch checked={d.enabled} onCheckedChange={(v) => setD({ ...d, enabled: v })} data-testid="admin-agent-discount-enabled" />Enabled</label>
        <div className="flex items-center gap-2 text-sm"><Input type="number" className="w-24 h-8" value={d.percent} onChange={(e) => setD({ ...d, percent: e.target.value })} data-testid="admin-agent-discount-percent" />% off</div>
        <Button size="sm" onClick={save} data-testid="admin-agent-discount-save">Save</Button>
      </div>
    </div>
  );
}

export default function AgentsAdmin() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/agents").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const toggle = async (a) => {
    try { await api.put(`/admin/agents/${a.id}`, { active: !a.active }); toast.success(a.active ? "Agent suspended" : "Agent reactivated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="admin-agents-tab">
      <DiscountCard value={data.discount} onSaved={load} />
      <div className="rounded-xl border border-slate-800 overflow-x-auto bg-white">
        <table className="w-full text-sm" data-testid="admin-agents-table">
          <thead className="bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400"><tr>{["Agent", "Contact", "Companies", "Projects", "Paid to date", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {data.agents.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No Marketing Agents yet.</td></tr>}
            {data.agents.map((a) => (
              <tr key={a.id} className="border-t border-slate-800" data-testid={`admin-agent-row-${a.id}`}>
                <td className="px-4 py-2.5 font-medium whitespace-nowrap">{a.name}</td>
                <td className="px-4 py-2.5 text-xs">{a.email}<div className="text-slate-500">{a.phone || ""}</div></td>
                <td className="px-4 py-2.5 text-xs max-w-xs">{a.companies.filter((c) => !c.is_agency).map((c) => c.name).join(", ") || "-"}<div className="text-slate-500">{a.companies.filter((c) => !c.is_agency).length} developer {a.companies.filter((c) => !c.is_agency).length === 1 ? "company" : "companies"}</div></td>
                <td className="px-4 py-2.5">{a.projects}</td>
                <td className="px-4 py-2.5 font-mono">{inr(a.paid_total)}</td>
                <td className="px-4 py-2.5"><Badge2 tone={a.active ? "green" : "rose"}>{a.active ? "active" : "suspended"}</Badge2></td>
                <td className="px-4 py-2.5"><Button size="sm" variant={a.active ? "destructive" : "default"} onClick={() => toggle(a)} data-testid={`admin-agent-toggle-${a.id}`}>{a.active ? "Suspend" : "Reactivate"}</Button></td>
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
